import React from 'react';
import Grid from '@material-ui/core/Grid';
import { FieldArray } from 'formik';
import PropTypes from 'prop-types';

import AddButton from 'core/components/add-button';
import { FormField } from 'core/components/form';
import { Label } from 'core/components/label';
import RemoveButton from 'core/components/remove-button';

const EMPTY_TRUSTEE = { name: '', address: { address: '', address2: '', city: '', state: '', zip: '' } };

/**
 * #36626: the trustees of a trust interested party (CFM auto, PA 1300), each with a name and an
 * address. `name` is the Formik path of the list, e.g. `lienHolders[0].trustees` on the offer card or
 * `trustees` in the add form. The street is a plain text field: the address autocomplete writes to
 * fixed sibling paths, which a nested list does not have.
 */
function Trustees({ name, trustees, disabled }) {
  return (
    <FieldArray
      name={name}
      render={(arrayHelpers) => (
        <Grid item xs={12}>
          <Grid container alignItems="center" spacing={4}>
            <Grid item xs={12}>
              <Label type="formSubTitle">Trustees</Label>
            </Grid>
            {(trustees ?? []).map((_, index) => (
              <React.Fragment key={index}>
                <FormField
                  mode="light"
                  name={`${name}.${index}.name`}
                  id={`${name}.${index}.name`}
                  label={`Trustee ${index + 1} name`}
                  type="string"
                  xs={11}
                  permissions={{ isLicensedAction: false }}
                />
                <Grid item xs={1}>
                  <RemoveButton disabled={disabled} onClick={() => arrayHelpers.remove(index)} />
                </Grid>
                <FormField
                  mode="light"
                  name={`${name}.${index}.address.address`}
                  id={`${name}.${index}.address.address`}
                  label="Trustee address"
                  type="string"
                  optional
                  xs={8}
                  permissions={{ isLicensedAction: false }}
                />
                <FormField
                  mode="light"
                  name={`${name}.${index}.address.address2`}
                  id={`${name}.${index}.address.address2`}
                  label="Address 2"
                  type="string"
                  optional
                  xs={4}
                  permissions={{ isLicensedAction: false }}
                />
                <FormField
                  mode="light"
                  name={`${name}.${index}.address.city`}
                  id={`${name}.${index}.address.city`}
                  label="City"
                  type="string"
                  optional
                  xs={4}
                  permissions={{ isLicensedAction: false }}
                />
                <FormField
                  mode="light"
                  name={`${name}.${index}.address.state`}
                  id={`${name}.${index}.address.state`}
                  label="State"
                  type="state"
                  optional
                  xs={4}
                  permissions={{ isLicensedAction: false }}
                />
                <FormField
                  mode="light"
                  name={`${name}.${index}.address.zip`}
                  id={`${name}.${index}.address.zip`}
                  label="Zip code"
                  type="string"
                  optional
                  xs={4}
                  permissions={{ isLicensedAction: false }}
                />
              </React.Fragment>
            ))}
            <Grid item xs={12}>
              <AddButton
                type="full"
                disabled={disabled}
                onClick={() => arrayHelpers.push(EMPTY_TRUSTEE)}
                label="Add a trustee"
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    />
  );
}

Trustees.propTypes = {
  name: PropTypes.string.isRequired,
  trustees: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      address: PropTypes.shape({
        address: PropTypes.string,
        address2: PropTypes.string,
        city: PropTypes.string,
        state: PropTypes.string,
        zip: PropTypes.string
      })
    })
  ),
  disabled: PropTypes.bool
};

Trustees.defaultProps = {
  trustees: [],
  disabled: false
};

export default Trustees;
